import React from 'react';
import { Quest, Reward } from '../types';
import { WorkingToward } from './WorkingToward';
import { fireQuestConfetti } from '../utils/confetti';
import { playSuccessChime } from '../utils/sound';

interface TodayViewProps {
  quests: Quest[];
  rewards: Reward[];
  selectedKidId: string;
  onToggleQuest: (questId: string) => void;
  onOpenAddQuest: () => void;
  onNavigateToRewards?: () => void;
}

export const TodayView: React.FC<TodayViewProps> = ({
  quests,
  rewards,
  selectedKidId,
  onToggleQuest,
  onOpenAddQuest,
  onNavigateToRewards,
}) => {
  const activeQuests = quests.filter((q) => q.is_active === 1);

  // Rewards ready for collection
  const relevantRewards = rewards.filter(
    (r) => r.is_shared === 1 || r.kid_id === selectedKidId
  );
  const readyRewards = relevantRewards.filter((r) => {
    const cost = r.star_cost;
    const progress = r.stars_progress || 0;
    const remaining = r.stars_remaining ?? Math.max(0, cost - progress);
    return remaining === 0 || progress >= cost;
  });

  const handleCheck = (e: React.MouseEvent<HTMLButtonElement>, quest: Quest) => {
    e.stopPropagation();
    if (!quest.is_completed_today) {
      fireQuestConfetti(e.currentTarget);
      playSuccessChime();
    }
    onToggleQuest(quest.id);
  };

  return (
    <div className="today-view">
      {/* Reward Ready Celebration Banner */}
      {readyRewards.length > 0 && (
        <div
          id="banner-reward-ready"
          className="reward-ready-banner"
          onClick={() => onNavigateToRewards?.()}
          role="alert"
          tabIndex={0}
        >
          <div className="reward-ready-emoji-box">
            <span>🎁</span>
            <span className="reward-ready-sparkle">✨</span>
          </div>
          <div className="reward-ready-text">
            <h4 className="reward-ready-title">
              {readyRewards.length === 1
                ? `${readyRewards[0].emoji} ${readyRewards[0].name} is ready to collect!`
                : `${readyRewards.length} Rewards are ready to collect! 🎉`}
            </h4>
            <p className="reward-ready-subtitle">
              You earned enough stars!
            </p>
          </div>
          <button
            id="btn-goto-redeem-tab"
            className="reward-ready-cta-btn"
            onClick={(e) => {
              e.stopPropagation();
              onNavigateToRewards?.();
            }}
            aria-label="Visit Rewards tab to take your award"
          >
            <span>Redeem Tab</span>
            <span>→</span>
          </button>
        </div>
      )}
      {activeQuests.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-emoji">🎯</div>
          <h3 className="empty-state-title">No quests for today!</h3>
          <p className="empty-state-subtitle">Add a quest to get started.</p>
          <button
            style={{
              marginTop: '16px',
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #a855f7, #6366f1)',
              color: '#fff',
              border: 'none',
              borderRadius: '16px',
              fontWeight: 800,
              fontSize: '14px',
              cursor: 'pointer',
            }}
            onClick={onOpenAddQuest}
          >
            + Add Quest
          </button>
        </div>
      ) : (
        <div className="quest-list">
          {activeQuests.map((quest) => {
            const isCompleted = Boolean(quest.is_completed_today);
            const repeatStr = quest.repeat_days.join(', ');

            return (
              <div
                key={quest.id}
                id={`quest-card-${quest.id}`}
                className={`quest-card ${isCompleted ? 'completed' : ''}`}
              >
                <div className="quest-icon-box">
                  <span>{quest.icon_emoji || '⭐'}</span>
                </div>

                <div className="quest-details">
                  <h4 className="quest-name">{quest.name}</h4>
                  <div className="quest-meta">
                    <span>{repeatStr}</span>
                    {quest.time_str && (
                      <>
                        <span>·</span>
                        <span>🕒 {quest.time_str}</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="quest-points-pill">
                  <span className="points-star">⭐</span>
                  <span className="points-val">{quest.points}</span>
                </div>

                <button
                  id={`btn-check-quest-${quest.id}`}
                  className={`quest-check-circle ${isCompleted ? 'checked' : ''}`}
                  onClick={(e) => handleCheck(e, quest)}
                  aria-label={isCompleted ? `Mark ${quest.name} as incomplete` : `Complete quest ${quest.name}`}
                  title={isCompleted ? 'Completed! Tap to undo' : 'Tap to complete quest'}
                >
                  <svg
                    className={`quest-check-icon ${isCompleted ? 'animate-check' : ''}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M5 13.5L9.5 18L19 7.5" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Working Toward Section directly underneath quests */}
      <WorkingToward
        rewards={rewards}
        selectedKidId={selectedKidId}
        onNavigateToRewards={onNavigateToRewards}
      />
    </div>
  );
};
